import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
};

/**
 * Very small markdown-lite renderer for placeholder content:
 * - Lines starting with "## " → h2
 * - Lines starting with "- " → bullet list
 * - **text** → bold
 * - `text` → inline code
 * - blank line → paragraph break
 */
const renderInline = (text) => {
  const parts = [];
  let remaining = text;
  let key = 0;
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      parts.push(
        <code
          key={key++}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            backgroundColor: '#F2E4C9',
            color: '#7A5A0F',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.92em'
          }}
        >
          {token.slice(1, -1)}
        </code>
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const renderContent = (content) => {
  const blocks = content.split(/\n\n+/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (trimmed.startsWith('## ')) {
      return (
        <h2
          key={i}
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
          className="text-3xl md:text-4xl leading-tight mt-10 mb-4"
        >
          {renderInline(trimmed.slice(3))}
        </h2>
      );
    }
    // Bullet list block: every line starts with "- "
    const lines = trimmed.split('\n');
    if (lines.every((l) => l.trim().startsWith('- '))) {
      return (
        <ul key={i} className="list-disc pl-6 mb-6 space-y-2">
          {lines.map((l, j) => (
            <li key={j} style={{ fontFamily: 'Manrope', color: '#3D3D3D', lineHeight: 1.75 }}>
              {renderInline(l.trim().slice(2))}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p
        key={i}
        style={{ fontFamily: 'Manrope', color: '#3D3D3D', lineHeight: 1.8 }}
        className="text-base md:text-lg mb-5"
      >
        {renderInline(trimmed)}
      </p>
    );
  });
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const scriptRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/${slug}`)
      .then(({ data }) => {
        setPost(data);
        document.title = `${data.title} · Eat From Local`;
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
    return () => {
      document.title = 'Eat From Local';
    };
  }, [slug]);

  // Inject BlogPosting JSON-LD for SEO / AI search
  useEffect(() => {
    if (!post) return;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      keywords: (post.keywords || []).join(', '),
      author: { '@type': 'Organization', name: post.author_name || 'Eat From Local' },
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      publisher: {
        '@type': 'Organization',
        name: 'Eat From Local',
        logo: { '@type': 'ImageObject', url: 'https://eatfromlocal.com/og-image.png' }
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `https://eatfromlocal.com/blog/${post.slug}` }
    };
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.text = JSON.stringify(jsonLd);
    s.setAttribute('data-blog-schema', 'true');
    document.head.appendChild(s);
    scriptRef.current = s;
    return () => {
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, [post]);

  if (loading) {
    return (
      <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }} className="flex items-center justify-center">
        <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>Loading…</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }} className="flex flex-col items-center justify-center p-6">
        <p style={{ fontFamily: 'Cormorant Garamond', fontSize: '32px', color: '#1A1A1A' }}>Post not found</p>
        <button
          onClick={() => navigate('/blog')}
          className="mt-4 underline"
          style={{ fontFamily: 'Manrope', color: '#D95A41' }}
        >
          Back to blog
        </button>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }} data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-4 md:px-6 pt-10 md:pt-16 pb-20">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 mb-8 hover:underline"
          style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#5C5C5C' }}
          data-testid="back-to-blog"
        >
          <ArrowLeft size={16} strokeWidth={1.5} /> Back to blog
        </Link>

        <div className="mb-3 flex flex-wrap items-center gap-3 text-sm" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
          <span className="flex items-center gap-1.5"><Calendar size={14} strokeWidth={1.5} />{formatDate(post.created_at)}</span>
          <span>· {post.author_name}</span>
        </div>

        <h1
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
          className="text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6"
          data-testid="post-title"
        >
          {post.title}
        </h1>

        {post.excerpt && (
          <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', fontStyle: 'italic' }} className="text-lg md:text-xl mb-10 border-l-2 border-[#D95A41] pl-4">
            {post.excerpt}
          </p>
        )}

        <div data-testid="post-content">
          {renderContent(post.content)}
        </div>

        {post.keywords && post.keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-12 pt-6 border-t border-[#E5E0D8]" data-testid="post-keywords">
            {post.keywords.map((k) => (
              <span
                key={k}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full"
                style={{ fontFamily: 'Manrope', fontSize: '12px', backgroundColor: '#F2E4C9', color: '#7A5A0F', fontWeight: 500 }}
              >
                <Tag size={10} strokeWidth={2} /> {k}
              </span>
            ))}
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPostPage;
